import React from "react";
import Select, { components } from "react-select";
import type { DropdownIndicatorProps, SingleValueProps } from "react-select";
import { ChevronsUpDown } from "lucide-react";
import { colorCodes } from "./TaskDetail/ColorPicker";

interface OptionType {
  id: string;
  name: string;
  [key: string]: any;
}

interface DropdownProps {
  options: OptionType[];
  value: string;
  defaultValue?: any;
  onChange: (id: string) => void;
  leftIcon?: React.ReactNode;
  placeholder?: string;
  showColor?: boolean;
  hideIndicatorUntilHover?: boolean; // Hide the dropdown indicator until the parent cell is hovered
}

const MenuPortal = (props) => {
  return <components.MenuPortal {...props} className="react-select-menu-portal" />;
};

// Custom DropdownIndicator with ChevronsUpDown icon
const DropdownIndicator = (props: DropdownIndicatorProps<OptionType, false>) => {
  return (
    <components.DropdownIndicator {...props}>
      <ChevronsUpDown className="w-3.5 h-3.5 text-gray-500" />
    </components.DropdownIndicator>
  );
};

// DropdownIndicator that is hidden until parent cell is hovered (uses Tailwind group/cell)
const HoverDropdownIndicator = (props: DropdownIndicatorProps<OptionType, false>) => {
  return (
    <components.DropdownIndicator {...props}>
      <div className="opacity-0 group-hover/cell:opacity-100 transition-opacity duration-150">
        <ChevronsUpDown className="w-3.5 h-3.5 text-gray-500" />
      </div>
    </components.DropdownIndicator>
  );
};

const DropdownField = (props: DropdownProps) => {
  const {
    options = [],
    value,
    onChange,
    leftIcon = null,
    placeholder = "Select...",
    showColor = false,
    hideIndicatorUntilHover = false,
  } = props;
  const selectedItem = value && options.find((option) => option.id === value);

  // Custom SingleValue to ensure consistent formatting - no icon in selected value
  const SingleValue = (props: SingleValueProps<OptionType, false>) => {
    return <components.SingleValue {...props}>{props.data.name}</components.SingleValue>;
  };

  return (
    <Select
      className={"w-full"}
      options={options}
      components={{
        DropdownIndicator: hideIndicatorUntilHover ? HoverDropdownIndicator : DropdownIndicator,
        SingleValue,
        MenuPortal,
      }}
      placeholder={placeholder}
      styles={{
        control: (base, _state) => ({
          ...base,
          border: "0 !important",
          boxShadow: "0 !important",
          minHeight: "28px",
          height: "28px",
          "&:hover": {
            border: "0 !important",
          },
        }),
        valueContainer: (base) => ({
          ...base,
          padding: "0 8px",
          height: "28px",
        }),
        input: (base) => ({
          ...base,
          margin: 0,
          padding: 0,
        }),
        singleValue: (base) => ({
          ...base,
          margin: 0,
        }),
        placeholder: (base) => ({
          ...base,
          margin: 0,
        }),
        indicatorSeparator: (base) => ({
          ...base,
          display: "none",
        }),
        dropdownIndicator: (base) => ({
          ...base,
          padding: "4px",
        }),
        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
      }}
      menuPlacement={"auto"}
      menuPortalTarget={document.body}
      menuPosition="fixed"
      value={selectedItem}
      isOptionSelected={(option, selectValue: any) => {
        return selectValue.id == option.id;
      }}
      formatOptionLabel={(option, { context }) => {
        // In the menu, show icon with text
        if (context === "menu") {
          let colorIcon = null;
          if (showColor) {
            const currentColor = colorCodes.find((cc) => cc.color === option.color);
            const colorCode = currentColor?.code;
            colorIcon = (
              <span
                className="block w-3.5 h-3.5 rounded-full shrink-0"
                style={{ backgroundColor: colorCode || "#d1d5db" }}
              />
            );
          }
          return (
            <div className={"w-full flex items-center space-x-2"} key={option.id}>
              {colorIcon}
              {leftIcon}
              <div>{option.name}</div>
            </div>
          );
        }
        // In the control (selected value), show only text to avoid indentation
        return option.name;
      }}
      onChange={(newValue) => {
        onChange(newValue.id);
      }}
    />
  );
};

export default DropdownField;
